import { styled, useTheme } from '@mui/material'
import React from 'react'
import { type IconProps, ToastContainer, type ToastContainerProps, type TypeOptions } from 'react-toastify'

const StyledInner = styled(ToastContainer)(({ theme }) => ({
  '& .Toastify__toast': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 14,
    minWidth: 300,
    padding: '13px 16px 13px 18px',
    borderRadius: 14,
    background: 'rgba(9, 24, 40, 0.96)',
    border: `1px solid ${theme.palette.border}`,
    boxShadow: '0 22px 54px -16px rgba(0, 0, 0, .6)',
    color: theme.palette.common.white,
    fontFamily: theme.tokens.secondaryFontFamily,
    fontSize: '0.75em',
    fontWeight: 500
  },

  '& .Toastify__toast-body': {
    margin: 0,
    padding: 0
  },

  // The custom badge below is 30px; drop the library icon's own width/margin so the
  // toast's `gap: 14` is the only spacing (matches the prototype's 14px).
  '& .Toastify__toast-icon': {
    width: 'auto',
    marginInlineEnd: 0
  },

  '& .Toastify__toast--success': {
    borderLeft: `4px solid ${theme.palette.success.main}`
  },

  '& .Toastify__toast--error': {
    borderLeft: `4px solid ${theme.palette.error.main}`
  },

  '& .Toastify__toast--warning': {
    borderLeft: `4px solid ${theme.palette.warning.main}`
  },

  '& .Toastify__toast--info': {
    borderLeft: `4px solid ${theme.palette.info.main}`
  }
}))

// Matches design-system.html `.toast .ic`: a type-coloured circle badge with a
// dark-navy glyph (the inverse of react-toastify v11's default coloured glyph).
const GLYPH = { success: '✓', error: '✕', warning: '!' } as const
const GLYPH_COLOR = '#08203b'

// Baked-in custom icon so every toast (all funnelled through this one container)
// gets the badge without touching call sites. Reads the MUI theme here — a real
// component under ThemeProvider — rather than inside the portaled toast.
// Omit `theme` — react-toastify's `Theme` ('light'|'dark'|…) collides with the MUI
// `Theme` on the styled component, and the container is never given one.
export default function StyledToastContainer (props: Omit<ToastContainerProps, 'theme'>) {
  const theme = useTheme()
  const color: Partial<Record<TypeOptions, string>> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main
  }

  const renderIcon = ({ type }: IconProps) => {
    const bg = color[type]
    const glyph = GLYPH[type as keyof typeof GLYPH]
    // info / default are never emitted — no icon.
    if (!bg || !glyph) return null
    return (
      <span style={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bg,
        color: GLYPH_COLOR,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1
      }}>{glyph}</span>
    )
  }

  return <StyledInner icon={renderIcon} {...props} />
}
